import isEmpty from "lodash-es/isEmpty"
import map from "lodash-es/map"

import type {
    IAttendanceParticipantModuleDto,
    IAttendanceParticipantModuleModel,
    IAttendanceSessionParticipantDto,
    IAttendanceSessionParticipantModel,
    ICoachingSessionParticipantDto,
    ICoachingSessionParticipantModel,
    IEnrollmentDto,
    IEnrollmentModel,
    ISessionExtraPartyDto,
    ISessionExtraPartyModel,
    ISessionParticipantDto,
    ISessionParticipantModel
} from "@/3514/types"
import { ESessionExtraPartyRole } from "@/3514/types"

import { cohortAvailableTagAdapter } from "./cohort.adapter"
import { enrollmentAdapter } from "./enrollment.adapter"
import { attendanceParticipantModuleAdapter } from "./module.adapter"
import { getSessionAttendanceStatusEnumFromType, getSessionInvitationStatusEnumFromType } from "./session.adapter"

function extraPartiesAdapter(dtos: ISessionExtraPartyDto[]): ISessionExtraPartyModel[] {
    return isEmpty(dtos)
        ? []
        : map<ISessionExtraPartyDto, ISessionExtraPartyModel>(
              dtos,
              (party: ISessionExtraPartyDto): ISessionExtraPartyModel => ({
                  id: party.id,
                  email: party.email,
                  lastName: party.last_name,
                  firstName: party.first_name,
                  label: party.extra_party_type_label,
                  role: ((): ESessionExtraPartyRole => {
                      switch (party.extra_party_type) {
                          case "hr":
                              return ESessionExtraPartyRole.Hr
                          case "direct-report":
                              return ESessionExtraPartyRole.DirectReport
                          case "peer":
                              return ESessionExtraPartyRole.Peer
                          case "manager":
                              return ESessionExtraPartyRole.Manager
                          case "participant":
                              return ESessionExtraPartyRole.Participant
                          default:
                              return null
                      }
                  })(),
                  attendanceStatus: getSessionAttendanceStatusEnumFromType(party.attendance_status),
                  invitationStatus: getSessionInvitationStatusEnumFromType(party.invitation_status)
              })
          )
}

function participantSessionBaseAdapter(dto: ISessionParticipantDto): ISessionParticipantModel {
    return isEmpty(dto)
        ? null
        : {
              firstName: dto.first_name,
              id: dto.id,
              isGuest: dto.is_guest,
              journeyLength: dto.journey_length,
              lastName: dto.last_name,
              photo: dto.photo
          }
}

function coachSessionParticipantsAdapter(dtos: ISessionParticipantDto[]): ISessionParticipantModel[] {
    return isEmpty(dtos)
        ? []
        : map<ISessionParticipantDto, ISessionParticipantModel>(dtos, participantSessionBaseAdapter)
}

function coachingSessionParticipantsAdapter(
    participants: ICoachingSessionParticipantDto[]
): ICoachingSessionParticipantModel[] {
    return isEmpty(participants)
        ? []
        : map<ICoachingSessionParticipantDto, ICoachingSessionParticipantModel>(
              participants,
              (p: ICoachingSessionParticipantDto): ICoachingSessionParticipantModel => ({
                  ...participantSessionBaseAdapter(p),
                  attendanceStatus: getSessionAttendanceStatusEnumFromType(p.attendance_status),
                  invitationStatus: getSessionInvitationStatusEnumFromType(p?.invitation?.status || null)
              })
          )
}

function attendanceSessionParticipantAdapter(
    dto: IAttendanceSessionParticipantDto
): IAttendanceSessionParticipantModel {
    return isEmpty(dto)
        ? null
        : {
              ...participantSessionBaseAdapter(dto),
              assignedFocusTag: cohortAvailableTagAdapter(dto.assigned_focus_tag),
              attendanceStatus: getSessionAttendanceStatusEnumFromType(dto.attendance_status),
              nextEnrollment: enrollmentAdapter(dto.assigned_next_module),
              enrollments: isEmpty(dto.enrolled_modules)
                  ? []
                  : map<IEnrollmentDto, IEnrollmentModel>(dto.enrolled_modules, enrollmentAdapter),
              modules: isEmpty(dto.modules)
                  ? []
                  : map<IAttendanceParticipantModuleDto, IAttendanceParticipantModuleModel>(
                        dto.modules,
                        attendanceParticipantModuleAdapter
                    )
          }
}

export {
    extraPartiesAdapter,
    participantSessionBaseAdapter,
    coachSessionParticipantsAdapter,
    coachingSessionParticipantsAdapter,
    attendanceSessionParticipantAdapter
}
